class BenefitsBar {
  constructor(element) {
    this.container = element;
    this.track = element.querySelector('.benefits-bar__track');
    this.items = Array.from(element.querySelectorAll('.benefits-bar__item:not(.benefits-bar__item--duplicate)'));
    this.dots = Array.from(element.querySelectorAll('.benefits-bar__dot'));
    this.arrows = {
      prev: element.querySelector('[data-arrow="prev"]'),
      next: element.querySelector('[data-arrow="next"]')
    };
    
    this.autoScrollDelay = 3000;
    this.isAutoScrolling = false;
    this.autoScrollInterval = null;
    this.autoScrollTimer = null;
    this.currentIndex = 0;
    
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.userHasInteracted = false;
    
    this.observer = null;
    this.isInView = false;
    
    this.isMobile = window.innerWidth < 750;
    
    this.init();
  }
  
  init() {
    if (!this.isMobile) {
      return;
    }
    
    console.log('Benefits Bar initialized on mobile');
    console.log('Items count:', this.items.length);
    
    this.setupIntersectionObserver();
    
    this.setupTouchEvents();
    
    this.setupDotNavigation();
    
    this.setupArrowNavigation();
    
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isInView = entry.isIntersecting;
        console.log('Section in view:', this.isInView, 'User interacted:', this.userHasInteracted);
        
        if (this.isInView && !this.userHasInteracted) {
          console.log('Starting auto-scroll');
          this.startAutoScroll();
        } else if (!this.isInView) {
          console.log('Stopping auto-scroll - section out of view');
          this.stopAutoScroll();
        }
      });
    }, options);
    
    this.observer.observe(this.container);
  }
  
  startAutoScroll() {
    if (this.isAutoScrolling || !this.isMobile) {
      console.log('Auto-scroll prevented:', { isAutoScrolling: this.isAutoScrolling, isMobile: this.isMobile });
      return;
    }
    
    console.log('Auto-scroll started');
    this.isAutoScrolling = true;
    
    const scrollToNext = () => {
      if (!this.isAutoScrolling || this.userHasInteracted) {
        console.log('Scroll cancelled:', { isAutoScrolling: this.isAutoScrolling, userHasInteracted: this.userHasInteracted });
        return;
      }
      
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      console.log('Auto-scrolling to index:', this.currentIndex);
      this.scrollToIndex(this.currentIndex, true);
      
      this.autoScrollTimer = setTimeout(scrollToNext, this.autoScrollDelay);
    };
    
    this.autoScrollTimer = setTimeout(scrollToNext, 1000);
  }
  
  stopAutoScroll() {
    this.isAutoScrolling = false;
    
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
    
    if (this.autoScrollTimer) {
      clearTimeout(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  }
  
  scrollToIndex(index, smooth = false) {
    if (!this.isMobile || !this.items[index]) return;
    
    const itemWidth = this.items[0].offsetWidth;
    const gap = 18;
    const offset = index * (itemWidth + gap);
    
    this.currentTranslate = -offset;
    this.prevTranslate = this.currentTranslate;
    
    // Remove any dragging class
    this.track.classList.remove('is-dragging');
    
    if (smooth) {
      // Force reflow to ensure transition applies
      this.track.style.transition = 'none';
      void this.track.offsetWidth;
      this.track.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    } else {
      this.track.style.transition = 'none';
    }
    
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    this.updateDots(index);
    
    if (smooth) {
      setTimeout(() => {
        this.track.style.transition = 'transform 0.3s ease-out';
      }, 600);
    }
  }
  
  setupTouchEvents() {
    this.track.addEventListener('mousedown', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('mousemove', this.touchMove.bind(this), { passive: true });
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('mouseleave', this.touchEnd.bind(this));
    
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));
    
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }
  
  touchStart(event) {
    if (!this.isMobile) return;
    
    // Don't start dragging if clicking on arrow buttons
    if (event.target.closest('.benefits-bar__arrow')) {
      return;
    }
    
    this.userHasInteracted = true;
    this.stopAutoScroll();
    
    this.isDragging = true;
    this.startX = this.getPositionX(event);
    this.track.classList.add('is-dragging');
    this.track.style.transition = 'none';
  }
  
  touchMove(event) {
    if (!this.isDragging || !this.isMobile) return;
    
    const currentPosition = this.getPositionX(event);
    const diff = currentPosition - this.startX;
    this.currentTranslate = this.prevTranslate + diff;
    
    const maxTranslate = 0;
    const minTranslate = -(this.track.scrollWidth - this.container.offsetWidth);
    this.currentTranslate = Math.max(Math.min(this.currentTranslate, maxTranslate), minTranslate);
    
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }
  
  touchEnd() {
    if (!this.isDragging || !this.isMobile) return;
    
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    
    const itemWidth = this.items[0].offsetWidth;
    const gap = 18;
    const itemFullWidth = itemWidth + gap;
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    
    if (Math.abs(movedBy) > itemWidth / 4) {
      if (movedBy > 0 && this.currentIndex > 0) {
        this.currentIndex--;
      } else if (movedBy < 0 && this.currentIndex < this.items.length - 1) {
        this.currentIndex++;
      }
    }
    
    // Smooth snap to position after drag
    this.track.style.transition = 'transform 0.3s ease-out';
    this.scrollToIndex(this.currentIndex, false);
  }
  
  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }
  
  setupDotNavigation() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.userHasInteracted = true;
        this.stopAutoScroll();
        this.currentIndex = index;
        this.scrollToIndex(index, true);
      });
    });
  }
  
  setupArrowNavigation() {
    if (!this.arrows.prev || !this.arrows.next) return;
    
    const handleArrowClick = (e, direction) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent any ongoing drag
      if (this.isDragging) {
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
      }
      
      this.userHasInteracted = true;
      this.stopAutoScroll();
      
      if (direction === 'prev') {
        if (this.currentIndex > 0) {
          this.currentIndex--;
        } else {
          this.currentIndex = this.items.length - 1;
        }
      } else {
        if (this.currentIndex < this.items.length - 1) {
          this.currentIndex++;
        } else {
          this.currentIndex = 0;
        }
      }
      
      this.scrollToIndex(this.currentIndex, true);
    };
    
    this.arrows.prev.addEventListener('click', (e) => handleArrowClick(e, 'prev'), { passive: false });
    this.arrows.prev.addEventListener('touchend', (e) => {
      if (this.isDragging) return;
      handleArrowClick(e, 'prev');
    }, { passive: false });
    
    this.arrows.next.addEventListener('click', (e) => handleArrowClick(e, 'next'), { passive: false });
    this.arrows.next.addEventListener('touchend', (e) => {
      if (this.isDragging) return;
      handleArrowClick(e, 'next');
    }, { passive: false });
  }
  
  updateDots(index) {
    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 750;
    
    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        this.init();
      } else {
        this.cleanup();
      }
    }
  }
  
  cleanup() {
    this.stopAutoScroll();
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.track.style.transform = '';
    this.track.style.transition = '';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const benefitsBars = document.querySelectorAll('[data-benefits-bar]');
  benefitsBars.forEach(bar => {
    new BenefitsBar(bar);
  });
});

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const benefitsBar = event.target.querySelector('[data-benefits-bar]');
    if (benefitsBar) {
      new BenefitsBar(benefitsBar);
    }
  });
}
